import {
  Valuation,
  ValuationMethod,
  ValuationSource,
  EstimateLogValuation,
  PopetyValuation,
  PriceHubbleValuation,
  WuestPartnerValuation,
  MultiProviderValuation,
  ValuationRange,
} from '@popety-io/types'

/**
 * PriceHubble returns lowercase grades (poor | medium | good). The previous
 * comparison against 'HIGH'/'MEDIUM' never matched, so every estimate rendered
 * one dot while the detail row beneath it printed "medium".
 */
export const mapPriceHubbleConfidence = (
  raw: unknown
): 1 | 2 | 3 | undefined => {
  switch (String(raw ?? '').toLowerCase()) {
    case 'good':
      return 3
    case 'medium':
      return 2
    case 'poor':
      return 1
    default:
      return undefined
  }
}

/**
 * Wuest grades the plausibility of its own market value with a traffic light.
 * It used to be discarded in favour of a hardcoded 2.
 */
export const mapWuestTrafficLight = (raw: unknown): 1 | 2 | 3 | undefined => {
  switch (String(raw ?? '').toUpperCase()) {
    case 'GREEN':
      return 3
    case 'YELLOW':
      return 2
    case 'RED':
      return 1
    default:
      return undefined
  }
}

export const parsePopetyValuation = (
  popety: PopetyValuation
): Valuation | null => {
  const value = popety.output?.output?.price || 0
  const range = popety.output?.output?.price_interval
    ? {
        min: popety.output.output.price_interval.lower,
        max: popety.output.output.price_interval.upper,
      }
    : undefined
  const confidence = popety.output?.confidence?.score || 3

  if (value <= 0) return null

  return {
    id: 'avm-popety',
    name: 'Popety.io Valuation',
    method: ValuationMethod.AVM,
    source: ValuationSource.POPETY_IO,
    value,
    range,
    confidence,
    description:
      "Automated valuation based on Popety.io's proprietary algorithm analyzing property characteristics, recent market transactions, and location-specific factors.",
    meta: {
      provider: 'popety.io',
      method: 'AVM',
      'Model Version': popety.output?.model?.version || 'Unknown',
      'Deal Type': popety.output?.model?.deal_type || 'Unknown',
      'Price per m²': popety.output?.output?.price_m2
        ? `CHF ${popety.output.output.price_m2.toLocaleString('de-CH')}`
        : 'N/A',
      'Confidence Score': popety.output?.confidence?.name || 'Unknown',
      'Listings Count': popety.output?.confidence?.listings_count || 0,
      'Data Range': `${popety.output?.model?.min_date || 'Unknown'} - ${popety.output?.model?.max_date || 'Unknown'}`,
    },
    createdAt: new Date(),
    updatedAt: new Date(),
    isVisible: true,
    isEditable: false,
  }
}

export const parsePriceHubbleValuation = (
  pricehubble: PriceHubbleValuation
): Valuation | null => {
  const range = pricehubble.output?.valueRange
    ? {
        min: pricehubble.output.valueRange.lower,
        max: pricehubble.output.valueRange.upper,
      }
    : undefined
  const value = range ? (range.min + range.max) / 2 : 0
  const confidence = mapPriceHubbleConfidence(pricehubble.output?.confidence)

  if (value <= 0) return null

  return {
    id: 'avm-pricehubble',
    name: 'PriceHubble Valuation',
    method: ValuationMethod.AVM,
    source: ValuationSource.PRICE_HUBBLE,
    value,
    range,
    confidence,
    description:
      'Automated valuation using PriceHubble AVM method based on machine learning algorithms and comprehensive market data.',
    meta: {
      provider: 'PriceHubble',
      method: 'AVM',
      'Confidence Level': pricehubble.output?.confidence || 'Unknown',
      Currency: pricehubble.output?.currency || 'CHF',
      'Property Type': `${pricehubble.input?.property?.propertyType?.code || 'Unknown'} - ${pricehubble.input?.property?.propertyType?.subcode || 'Unknown'}`,
      'Deal Type': pricehubble.input?.dealType || 'Unknown',
      'Building Year': pricehubble.input?.property?.buildingYear || 'Unknown',
      'Living Area': pricehubble.input?.property?.livingArea
        ? `${pricehubble.input.property.livingArea} m²`
        : 'Unknown',
      'Number of Rooms':
        pricehubble.input?.property?.numberOfRooms || 'Unknown',
    },
    createdAt: new Date(),
    updatedAt: new Date(),
    isVisible: true,
    isEditable: false,
  }
}

export const parseWuestPartnerValuation = (
  wuest: WuestPartnerValuation
): Valuation[] => {
  const valuations: Valuation[] = []

  // Merge pessimist and optimist scenarios into a single valuation with range
  const pessimistValue =
    wuest.output?.pessimist?.keyFigures?.marketValueAfterCorrection || 0
  const optimistValue =
    wuest.output?.optimist?.keyFigures?.marketValueAfterCorrection || 0

  if (pessimistValue > 0 && optimistValue > 0) {
    // Calculate average value as the middle price
    const averageValue = (pessimistValue + optimistValue) / 2

    valuations.push({
      id: 'avm-wuest',
      name: 'Wüest Partner Valuation',
      method: ValuationMethod.AVM,
      source: ValuationSource.WUEST_PARTNER,
      value: averageValue,
      range: {
        min: pessimistValue, // Conservative (low) estimate
        max: optimistValue, // Optimistic (high) estimate
      },
      confidence:
        mapWuestTrafficLight(
          wuest.output?.pessimist?.keyFigures?.marketValueTrafficLight
        ) ??
        mapWuestTrafficLight(
          wuest.output?.optimist?.keyFigures?.marketValueTrafficLight
        ),
      description:
        'Automated valuation using Wüest Partner methodology with market range from conservative to optimistic scenarios.',
      meta: {
        provider: 'Wüest Partner',
        method: 'AVM',
        'Market Value Type':
          wuest.output.pessimist?.keyFigures?.marketValueType ||
          wuest.output.optimist?.keyFigures?.marketValueType ||
          'Unknown',
        'Conservative Estimate': `CHF ${pessimistValue.toLocaleString('de-CH')}`,
        'Optimistic Estimate': `CHF ${optimistValue.toLocaleString('de-CH')}`,
        'Average Estimate': `CHF ${averageValue.toLocaleString('de-CH')}`,
        'Value Range': `CHF ${pessimistValue.toLocaleString('de-CH')} - CHF ${optimistValue.toLocaleString('de-CH')}`,
        // keyFigures.area is the land plot, so these are per land m², never per
        // living m². Naming them explicitly stops them being read as comparable
        // to Popety.io's living-area figure.
        'Value per m² of land (Conservative)': wuest.output.pessimist
          ?.keyFigures?.marketValueAfterCorrectionByArea
          ? `CHF ${wuest.output.pessimist.keyFigures.marketValueAfterCorrectionByArea.toLocaleString('de-CH')}`
          : 'N/A',
        'Value per m² of land (Optimistic)': wuest.output.optimist?.keyFigures
          ?.marketValueAfterCorrectionByArea
          ? `CHF ${wuest.output.optimist.keyFigures.marketValueAfterCorrectionByArea.toLocaleString('de-CH')}`
          : 'N/A',
        'Land area': wuest.output.pessimist?.keyFigures?.area
          ? `${wuest.output.pessimist.keyFigures.area} m²`
          : wuest.output.optimist?.keyFigures?.area
            ? `${wuest.output.optimist.keyFigures.area} m²`
            : 'Unknown',
        Method:
          wuest.output.pessimist?.method ||
          wuest.output.optimist?.method ||
          'Unknown',
        Status:
          wuest.output.pessimist?.status ||
          wuest.output.optimist?.status ||
          'Unknown',
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isVisible: true,
      isEditable: false,
    })
  } else if (pessimistValue > 0) {
    // Only pessimist value available
    valuations.push({
      id: 'avm-wuest',
      name: 'Wüest Partner Valuation',
      method: ValuationMethod.AVM,
      source: ValuationSource.WUEST_PARTNER,
      value: pessimistValue,
      confidence:
        mapWuestTrafficLight(
          wuest.output?.pessimist?.keyFigures?.marketValueTrafficLight
        ) ??
        mapWuestTrafficLight(
          wuest.output?.optimist?.keyFigures?.marketValueTrafficLight
        ),
      description:
        'Conservative automated valuation using Wüest Partner methodology.',
      meta: {
        provider: 'Wüest Partner',
        method: 'AVM',
        scenario: 'Conservative',
        'Market Value Type':
          wuest.output.pessimist.keyFigures?.marketValueType || 'Unknown',
        'Value Before Correction': wuest.output.pessimist.keyFigures
          ?.marketValueBeforeCorrection
          ? `CHF ${wuest.output.pessimist.keyFigures.marketValueBeforeCorrection.toLocaleString('de-CH')}`
          : 'N/A',
        'Value per m²': wuest.output.pessimist.keyFigures
          ?.marketValueAfterCorrectionByArea
          ? `CHF ${wuest.output.pessimist.keyFigures.marketValueAfterCorrectionByArea.toLocaleString('de-CH')}`
          : 'N/A',
        'Land area': wuest.output.pessimist.keyFigures?.area
          ? `${wuest.output.pessimist.keyFigures.area} m²`
          : 'Unknown',
        Method: wuest.output.pessimist.method || 'Unknown',
        Status: wuest.output.pessimist.status || 'Unknown',
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isVisible: true,
      isEditable: false,
    })
  } else if (optimistValue > 0) {
    // Only optimist value available
    valuations.push({
      id: 'avm-wuest',
      name: 'Wüest Partner Valuation',
      method: ValuationMethod.AVM,
      source: ValuationSource.WUEST_PARTNER,
      value: optimistValue,
      confidence:
        mapWuestTrafficLight(
          wuest.output?.pessimist?.keyFigures?.marketValueTrafficLight
        ) ??
        mapWuestTrafficLight(
          wuest.output?.optimist?.keyFigures?.marketValueTrafficLight
        ),
      description:
        'Optimistic automated valuation using Wüest Partner methodology.',
      meta: {
        provider: 'Wüest Partner',
        method: 'AVM',
        scenario: 'Optimistic',
        'Market Value Type':
          wuest.output.optimist.keyFigures?.marketValueType || 'Unknown',
        'Value Before Correction': wuest.output.optimist.keyFigures
          ?.marketValueBeforeCorrection
          ? `CHF ${wuest.output.optimist.keyFigures.marketValueBeforeCorrection.toLocaleString('de-CH')}`
          : 'N/A',
        'Value per m²': wuest.output.optimist.keyFigures
          ?.marketValueAfterCorrectionByArea
          ? `CHF ${wuest.output.optimist.keyFigures.marketValueAfterCorrectionByArea.toLocaleString('de-CH')}`
          : 'N/A',
        'Land area': wuest.output.optimist.keyFigures?.area
          ? `${wuest.output.optimist.keyFigures.area} m²`
          : 'Unknown',
        Method: wuest.output.optimist.method || 'Unknown',
        Status: wuest.output.optimist.status || 'Unknown',
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isVisible: true,
      isEditable: false,
    })
  }

  return valuations
}

export const parseLogValuationData = (
  logValuation: EstimateLogValuation
): Valuation[] => {
  const parsedValuations: Valuation[] = []

  // Handle Popety.io valuation
  if (logValuation['popety.io']) {
    const popetyValuation = parsePopetyValuation(logValuation['popety.io'])
    if (popetyValuation) {
      parsedValuations.push(popetyValuation)
    }
  }

  // Handle PriceHubble valuation
  if (logValuation.PriceHubble) {
    const priceHubbleValuation = parsePriceHubbleValuation(
      logValuation.PriceHubble
    )
    if (priceHubbleValuation) {
      parsedValuations.push(priceHubbleValuation)
    }
  }

  // Handle Wüest Partner valuation
  if (logValuation['Wüest Partner']) {
    const wuestValuations = parseWuestPartnerValuation(
      logValuation['Wüest Partner']
    )
    parsedValuations.push(...wuestValuations)
  }

  // Handle manual valuations
  if ((logValuation as any).manual) {
    const manualValuations = (logValuation as any).manual
    Object.entries(manualValuations).forEach(
      ([id, manualVal]: [string, any]) => {
        const parsedManual: Valuation = {
          id: manualVal.id || id,
          name: manualVal.formData?.name || 'Custom Valuation',
          method: manualVal.method || ValuationMethod.CUSTOM,
          source: ValuationSource.MANUAL,
          value: manualVal.value || manualVal.calculatedValue || 0,
          range: manualVal.formData?.range,
          confidence: manualVal.formData?.confidence || 2,
          description:
            manualVal.formData?.description ||
            `Custom valuation using ${(manualVal.method || 'custom').replace(/_/g, ' ').toLowerCase()} method`,
          meta: manualVal.formData?.meta || manualVal.formData || {},
          createdAt: manualVal.createdAt
            ? new Date(manualVal.createdAt)
            : new Date(),
          updatedAt: manualVal.updatedAt
            ? new Date(manualVal.updatedAt)
            : new Date(),
          isVisible: true,
          isEditable: true,
        }
        parsedValuations.push(parsedManual)
      }
    )
  }

  return parsedValuations
}

/**
 * Parse MultiProviderValuation data from AVM API endpoint
 * This handles the processed valuation data returned by /avm/valuation/:id
 */
export const parseMultiProviderValuation = (
  multiValuation: MultiProviderValuation
): Valuation[] => {
  const parsedValuations: Valuation[] = []

  // Handle Popety.io valuation
  if (multiValuation['Popety.io']) {
    const provider = multiValuation['Popety.io']
    parsedValuations.push({
      id: 'avm-popety-processed',
      name: 'Popety.io Valuation',
      method: ValuationMethod.AVM,
      source: ValuationSource.POPETY_IO,
      value: provider.price,
      range: {
        min: provider.valueRange.lower,
        max: provider.valueRange.upper,
      },
      confidence: Math.round(provider.confidence * 3), // Convert 0-1 scale to 1-3 scale
      description:
        provider.description ||
        "Automated valuation based on Popety.io's proprietary algorithm",
      meta: {
        provider: 'Popety.io',
        method: 'AVM',
        'Confidence Score': `${Math.round(provider.confidence * 100)}%`,
        'Value Range': `CHF ${provider.valueRange.lower.toLocaleString('de-CH')} - CHF ${provider.valueRange.upper.toLocaleString('de-CH')}`,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isVisible: true,
      isEditable: false,
    })
  }

  // Handle PriceHubble valuation
  if (multiValuation['PriceHubble']) {
    const provider = multiValuation['PriceHubble']
    parsedValuations.push({
      id: 'avm-pricehubble-processed',
      name: 'PriceHubble Valuation',
      method: ValuationMethod.AVM,
      source: ValuationSource.PRICE_HUBBLE,
      value: provider.price,
      range: {
        min: provider.valueRange.lower,
        max: provider.valueRange.upper,
      },
      confidence: Math.round(provider.confidence * 3), // Convert 0-1 scale to 1-3 scale
      description:
        provider.description ||
        'Automated valuation using PriceHubble AVM method',
      meta: {
        provider: 'PriceHubble',
        method: 'AVM',
        'Confidence Score': `${Math.round(provider.confidence * 100)}%`,
        'Value Range': `CHF ${provider.valueRange.lower.toLocaleString('de-CH')} - CHF ${provider.valueRange.upper.toLocaleString('de-CH')}`,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isVisible: true,
      isEditable: false,
    })
  }

  // Handle Wüest Partner valuation
  if (multiValuation['Wüest Partner']) {
    const provider = multiValuation['Wüest Partner']
    parsedValuations.push({
      id: 'avm-wuest-processed',
      name: 'Wüest Partner Valuation',
      method: ValuationMethod.AVM,
      source: ValuationSource.WUEST_PARTNER,
      value: provider.price,
      range: {
        min: provider.valueRange.lower,
        max: provider.valueRange.upper,
      },
      confidence: Math.round(provider.confidence * 3), // Convert 0-1 scale to 1-3 scale
      description:
        provider.description ||
        'Automated valuation using Wüest Partner methodology',
      meta: {
        provider: 'Wüest Partner',
        method: 'AVM',
        'Confidence Score': `${Math.round(provider.confidence * 100)}%`,
        'Value Range': `CHF ${provider.valueRange.lower.toLocaleString('de-CH')} - CHF ${provider.valueRange.upper.toLocaleString('de-CH')}`,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isVisible: true,
      isEditable: false,
    })
  }

  return parsedValuations
}

/**
 * Parse manual valuations from estimate price data
 * Manual valuations are stored in price.manual object
 */
export const parseManualValuations = (priceData: any): Valuation[] => {
  const parsedValuations: Valuation[] = []

  // Extract manual valuations from price.manual object
  if (priceData?.manual && typeof priceData.manual === 'object') {
    Object.entries(priceData.manual).forEach(
      ([id, manualVal]: [string, any]) => {
        // Parse range: prioritize structured object, fallback to parsing meta string
        let range: ValuationRange | undefined = manualVal.formData?.range

        // Backward compatibility: parse old string format from meta
        if (!range && manualVal.formData?.meta?.['Value Range']) {
          const rangeStr = manualVal.formData.meta['Value Range']
          const match = rangeStr.match(/CHF\s*([\d,]+)\s*-\s*CHF\s*([\d,]+)/)
          if (match) {
            range = {
              min: parseInt(match[1].replace(/,/g, ''), 10),
              max: parseInt(match[2].replace(/,/g, ''), 10),
            }
          }
        }

        const parsedManual: Valuation = {
          id: manualVal.id || id,
          name: manualVal.formData?.name || 'Custom Valuation',
          method: manualVal.method || ValuationMethod.CUSTOM,
          source: ValuationSource.MANUAL,
          value: manualVal.value || manualVal.calculatedValue || 0,
          range: range,
          confidence: manualVal.formData?.confidence || 2,
          description:
            manualVal.formData?.description ||
            `Custom valuation using ${(manualVal.method || 'custom').replace(/_/g, ' ').toLowerCase()} method`,
          meta: manualVal.formData || {},
          color: manualVal.formData?.color, // Custom color for chart display
          createdAt: manualVal.createdAt
            ? new Date(manualVal.createdAt)
            : new Date(),
          updatedAt: manualVal.updatedAt
            ? new Date(manualVal.updatedAt)
            : new Date(),
          isVisible: true,
          isEditable: true,
        }
        parsedValuations.push(parsedManual)
      }
    )
  }

  return parsedValuations
}
